import {
  Component,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  ContentChild,
  TemplateRef,
} from '@angular/core'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { MatListOption } from '@angular/material/list'
import { ENTER } from '@angular/cdk/keycodes'

interface OptionList<T> {
  label: string
  value: T
}

@Component({
  selector: 'lin-form-checkbox-search-list',
  templateUrl: './checkbox-search-list.component.html',
  styleUrls: ['./checkbox-search-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxSearchListComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CheckboxSearchListComponent<T> implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @HostBinding('attr.disabled')
  @Input()
  set disabled(value) {
    this._disabled = coerceBooleanProperty(value)
  }
  get disabled() {
    return this._disabled
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() matchWith: (value: any) => string = String
  @Input() items: T[]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild(TemplateRef, { static: true }) templateRef: TemplateRef<any>

  optionList: OptionList<T>[]
  filteredOptionList: OptionList<T>[]
  searchInput = new FormControl()
  selectedItems: T[] = []

  private _disabled: boolean
  private readonly onDestroy$ = new Subject<void>()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onChange: (value: any) => void

  ngOnInit(): void {
    this.setOptions(this.items)
    this.searchInput.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(this.filterList)
  }

  ngOnChanges(changes: SimpleChanges): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    changes.items && this.setOptions(changes.items.currentValue)
  }

  setOptions(items: T[]): void {
    const toOptionList = (option: T): OptionList<T> => ({ label: this.matchWith(option), value: option })
    this.optionList = items?.map(toOptionList)
    this.filteredOptionList = this.optionList

    this.clearSearch()
  }

  filterList = (searchString: string) => {
    const searchRegExp = new RegExp(searchString, 'i')
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const bySearchString = ({ label }) => searchRegExp.test(label)

    this.filteredOptionList = this.optionList?.filter(bySearchString)
  }

  clearSearch(): void {
    this.searchInput.setValue('')
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    const { keyCode } = event

    if (keyCode === ENTER) event.preventDefault()
  }

  onSelectionChange({ option }: { option: MatListOption }): void {
    if (option.selected) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.selectedItems.push(option.value)
    } else {
      const isNotDeleted = (item: T) => item !== option.value
      this.selectedItems = this.selectedItems.filter(isNotDeleted)
    }

    this.onChange(this.selectedItems)
  }

  onOptionClicked(option: OptionList<T>): void {
    const value = option.value
    const index = this.selectedItems.indexOf(value)

    if (index === -1) {
      this.selectedItems.push(value)
    } else {
      this.selectedItems.splice(index, 1)
    }

    this.onChange(this.selectedItems)
  }

  isSelected(item: T): boolean {
    return this.selectedItems.includes(item)
  }

  writeValue(itemList: T[]): void {
    this.selectedItems = itemList || []
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.onChange = fn
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  registerOnTouched(fn: any): void {
    // TODO not implemented
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
